import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DatamuseService from "../../../services/DatamuseService";
import ConfirmDialog from "../../components/ConfirmDialog";
import StepFooter from "../../pzl_common/board/StepFooter";
import PuzzleWorkflowStepper from "../../pzl_common/board/PuzzleWorkflowStepper";
import BorderHeader from "../../pzl_common/BorderHeader";
import StepPuzzleWordPanel from "../../pzl_common/board/StepPuzzleWordPanel";
import BorderLeftPanel from "../../pzl_common/BorderLeftPanel";
import AdminWordWheelBoardService, { DRAFT, REVIEWED } from "./AdminWordWheelBoardService";
import Logger from "../../../services/Logger";
import { useWordWheelPuzzleData } from "./useWordWheelPuzzleData";
import { WORD_WHEEL_GRID_SIZE } from "./wordWheelGridConstants";

export default function Step2ReadyToReview() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [refresh, setRefresh] = useState(0);
    const { tpl: puzzle, loading, error: fetchError } = useWordWheelPuzzleData(id, refresh);
    const [error, setError] = useState(null);

    // Loading states
    const [loadingReview, setLoadingReview] = useState(false);
    const [loadingGoBackDraft, setLoadingGoBackDraft] = useState(false);
    const [loadingDiscard, setLoadingDiscard] = useState(false);

    // Confirmation dialog state for discarding template
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    // Function to initialize the square array
    const initializeSquare = (filledCoordinates) => {
        const size = WORD_WHEEL_GRID_SIZE;
        const newSquare = Array.from({ length: size }, () => Array.from({ length: size }, () => ""));

        for (let row = 0; row < size; row++) {
            for (let col = 0; col < size; col++) {
                newSquare[row][col] = findCharacter(row, col, filledCoordinates);
            }
        }

        return newSquare;
    };

    const findCharacter = (row, col, filledCoordinates) => {
        if (filledCoordinates) {
            for (const coord of filledCoordinates) {
                const matchingPosition = coord.positions.find(
                    (p) => p.row === row && p.col === col
                );
                if (matchingPosition && matchingPosition.value) {
                    return matchingPosition.value.toUpperCase();
                }
            }
        }
        return "";
    };

    // Parse puzzle grid from filledCoordinates
    const parsePuzzleGrid = () => {
        if (!puzzle?.filledCoordinates) return [];

        try {
            const filledCoordinates = JSON.parse(puzzle.filledCoordinates);
            return initializeSquare(filledCoordinates);
        } catch (error) {
            Logger.error('Error parsing filled coordinates:', error);
            return [];
        }
    };



    const handleReview = async () => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        setLoadingReview(true);
        
        try {
            setError(null);

            // Get word list from filledCoordinates
            const wordList = puzzle.filledCoordinates ? JSON.parse(puzzle.filledCoordinates).map(item => item.text) : [];

            // Get word scores from DatamuseService
            const wordScores = await DatamuseService.requestWordScores(wordList);

            // Call API to change state to REVIEWED with details
            await AdminWordWheelBoardService.updateTplState(puzzle.id, REVIEWED, wordScores);
            navigate(`/admin/puzzle/word_wheel_board/reviewed/${puzzle.id}`);
        } catch (err) {
            Logger.error("Error updating puzzle state:", err);
            setError(err?.message || "Failed to review puzzle. Please try again.");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
            setLoadingReview(false);
        }
    };

    const handleGoBackDraft = async () => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        setLoadingGoBackDraft(true);
        
        try {
            setError(null);
            // Call API to change state back to DRAFT
            await AdminWordWheelBoardService.updateTplState(puzzle.id, DRAFT);
            // Navigate back to draft step
            navigate(`/admin/puzzle/word_wheel_board/draft/${puzzle.id}`);
        } catch (err) {
            Logger.error("Error moving puzzle back to draft:", err);
            setError("Failed to move puzzle back to draft. Please try again.");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
            setLoadingGoBackDraft(false);
        }
    };

    const handleBack = () => {
        const fromStep = searchParams.get('fromStep');
        if (fromStep !== null) {
            navigate(`/admin/puzzle/word_wheel_board?activeStep=${fromStep}`);
        } else {
            navigate("/admin/puzzle/word_wheel_board");
        }
    };

    const handleDiscardTemplate = () => {
        // Show confirmation dialog instead of immediately discarding
        setOpenConfirmDialog(true);
    };

    const handleConfirmDiscard = async (targetId) => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        setLoadingDiscard(true);
        
        try {
            setError(null);
            // Call API to mark template as discarded
            await AdminWordWheelBoardService.markAsDiscarded(targetId);
            // Show success message
            // Note: You might want to add a snackbar component here
            // Navigate back to puzzle list
            navigate("/admin/puzzle/word_wheel_board");
        } catch (err) {
            Logger.error("Error discarding template:", err);
            setError("Failed to discard template. Please try again.");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
            setLoadingDiscard(false);
            setOpenConfirmDialog(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 400,
                p: 3
            }}>
                <Typography variant="h6">Loading puzzle data...</Typography>
            </Box>
        );
    }

    if (fetchError || !puzzle) {
        return (
            <Box sx={{ mt: 2, p: 3 }}>
                <Alert severity="error">
                    {fetchError || "Puzzle not found"}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%" }}>
            {/* Workflow Stepper */}
            <PuzzleWorkflowStepper
                currentState={puzzle?.state}
                puzzleId={puzzle?.id}
                size="small"
                align="left"
                onStepClick={() => setRefresh(prev => prev + 1)}
                puzzleType={puzzle?.type}
            />
            
            {/* Header with back button */}
            <BorderHeader
                onBack={handleBack}
                puzzle={puzzle}
                puzzleType="crossword"
            />

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
                {/* First Column: Puzzle Information */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                    <BorderLeftPanel
                        puzzle={puzzle}
                        onRefresh={() => setRefresh(prev => prev + 1)}
                        puzzleType="WORD_WHEEL"
                    />
                </Grid>

                {/* Second Column: Words List / Word Network */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                    <StepPuzzleWordPanel
                        puzzle={puzzle}
                        parsePuzzleGrid={parsePuzzleGrid}
                        enableScatterLayout
                        onRefresh={() => setRefresh((prev) => prev + 1)}
                    />
                </Grid>

            </Grid>

            {/* Step Footer with Action Buttons */}
            <StepFooter
                stepType="ready-for-review"
                title="Ready to Review"
                description="Review the puzzle content and approve or send back for revisions."
                onDiscard={handleDiscardTemplate}
                onReview={handleReview}
                onGoBackDraft={handleGoBackDraft}
                loadingReview={loadingReview}
                loadingGoBackDraft={loadingGoBackDraft}
                loadingDiscard={loadingDiscard}
            />

            {/* Confirmation Dialog for Discarding Template */}
            <ConfirmDialog
                open={openConfirmDialog}
                setOpen={setOpenConfirmDialog}
                title="Confirm Discard Template"
                message="Are you sure you want to discard this template? This action cannot be undone."
                targetName={`'${puzzle?.title || 'Untitled Template'}' will be discarded`}
                targetId={puzzle?.id}
                callback={handleConfirmDiscard}
            />
        </Box>
    );
}

